<template>
  <div
    class="game-screen cb-page cb-page--fixed"
    :class="[`game-screen--${phase}`, `game-screen--layout-${layout}`, {
      'game-screen--checking': checking,
      'game-screen--modern': modern,
      'game-screen--compact-lives': compactLives,
      'game-screen--signal': alertVisible,
    }]"
    :data-layout="layout"
    :data-post-round="postRoundStage || undefined"
  >
    <!-- These two live video elements keep their DOM identity across all layouts and post-round phases. -->
    <section class="game-stage" :inert="showRematchDialog || undefined" :aria-hidden="phase === 'intro' ? 'true' : undefined">
      <header v-if="!modern" class="game-stage__header">
        <button type="button" class="game-stage__back" aria-label="Уйти из игры" @click="askLeave">
          <AppIcon name="chevron-left" :size="24" />
        </button>
        <h1 class="game-stage__title" :title="`Режим: ${modeLabel}`">{{ stageTitle }}</h1>
      </header>
      <header v-else class="game-toolbar" :class="{ 'game-toolbar--moment': compactLives }">
        <h1 v-if="compactLives" class="game-toolbar__moment-title">Момент проигрыша</h1>
        <template v-else>
          <span class="game-toolbar__mode"><AppIcon :name="mode === 'MOUTH' ? 'mouth' : 'eye'" :size="22" />{{ modeLabel }}</span>
          <span class="game-toolbar__status" :class="{ 'game-toolbar__status--warning': connectionWeak || checking }" role="status">
            <span class="game-status-dot" aria-hidden="true"></span>
            <span>{{ alertVisible ? 'Нарушение?' : checking ? 'Проверка' : connectionWeak ? 'Связь…' : 'Игра' }}</span>
          </span>
          <span class="game-toolbar__time cb-num" role="timer" :aria-label="`Время раунда ${formatTimer(elapsed)}`">{{ formatTimer(elapsed) }}</span>
          <button
            type="button"
            class="game-toolbar__layout"
            :aria-label="layout === 'B' ? 'Показать крупное видео соперника, компоновка С' : 'Показать два равных видео, компоновка Б'"
            :title="layout === 'B' ? 'Компоновка Б: равные окна' : 'Компоновка С: крупный соперник'"
            :disabled="Boolean(postRoundStage)"
            @click="toggleLayout"
          >
            <span class="game-toolbar__layout-icon" :class="{ 'game-toolbar__layout-icon--large': layout === 'C' }" aria-hidden="true"><i></i><i></i></span>
            <span>{{ layout === 'B' ? 'Б' : 'С' }}</span>
          </button>
        </template>
      </header>

      <div class="game-stage__videos">
        <GameMomentPanel
          v-if="compactLives"
          class="game-stage__moment"
          :suspense="postRoundStage === 'suspense'"
          :frame-url="replayFrameUrl"
          :holding="replayHolding"
          :zoom-progress="replayZoomProgress"
          :region="replayRegion"
          :key-frame-only="replayKeyFrameOnly"
          :playing="replayPlaying"
          :unavailable="replayUnavailable"
          :loser-name="postRoundStage === 'suspense' ? '' : loserName"
          :reason="postRoundStage === 'suspense' ? '' : lossReason"
          @image-error="onReplayImageError"
        />

        <div v-if="compactLives" class="game-stage__live-status">
          <span><span class="game-status-dot" aria-hidden="true"></span>Вы на связи</span>
          <span v-if="postRoundStage === 'chat'" class="game-stage__deadline cb-num">{{ rematchPending ? 'Ответ · ' : 'Ещё ' }}{{ postRoundSeconds }} с</span>
          <span v-else class="game-stage__live-hint">Живой эфир</span>
        </div>

        <CornerFrame fill :border="!modern" :glow="!modern" :corners="!modern" class="game-stage__frame game-stage__frame--remote" :radius="modern ? 16 : 22" :corner-size="34" :inset="18">
          <video
            ref="remoteVideo"
            class="game-stage__video"
            playsinline
            autoplay
            @loadeddata="onRemoteFrame"
            @playing="onRemoteFrame"
          ></video>
          <template #overlay>
            <FaceGuideOverlay :visible="phase === 'calibration' && remoteVisible" />
            <Transition name="cb-fade">
              <div v-if="!remoteVisible" class="game-stage__waiting">
                <span class="cb-spinner game-stage__waiting-spinner" aria-hidden="true"></span>
                <span>Подключаем соперника…</span>
              </div>
            </Transition>
            <GameRematchOverlay v-if="compactLives && isRematchInitiator(opponent)" :progress="rematchProgress" />
            <span v-if="compactLives" class="game-stage__live-badge"><span class="game-status-dot" aria-hidden="true"></span>Эфир</span>
            <span class="game-stage__name game-stage__name--top cb-ellipsis">{{ opponentName }}</span>
            <Transition name="cb-fade">
              <span v-if="connectionWeak" class="game-stage__chip game-stage__chip--below">
                <AppIcon name="warning" :size="16" /> Связь прерывается…
              </span>
            </Transition>
          </template>
        </CornerFrame>

        <GameCenterBar
          v-show="!modern && !(phase === 'calibration' && countdown <= 3)"
          class="game-stage__center"
          :mode="centerMode"
          :value="centerValue"
          :progress="centerProgress"
          :ring="centerRing"
        />

        <CornerFrame fill :border="!modern" :glow="!modern" :corners="!modern" class="game-stage__frame game-stage__frame--local" :radius="modern ? 16 : 22" :corner-size="34" :inset="18">
          <video ref="localVideo" class="game-stage__video game-stage__video--mirror" playsinline autoplay muted></video>
          <template #overlay>
            <FaceGuideOverlay :visible="showLocalGuide" :active="faceDetected" />
            <GameRematchOverlay v-if="compactLives && isRematchInitiator(me)" :progress="rematchProgress" />
            <span v-if="compactLives" class="game-stage__live-badge"><span class="game-status-dot" aria-hidden="true"></span>Эфир</span>
            <span class="game-stage__name cb-ellipsis">{{ modern ? selfName : 'Вы' }}</span>
            <span v-if="modern && !postRoundStage" class="game-stage__face-status" :class="{ 'game-stage__face-status--warning': !faceDetected }" role="status">
              <span class="game-status-dot" aria-hidden="true"></span>{{ faceDetected ? 'Лицо в кадре' : 'Лицо не в кадре' }}
            </span>
            <Transition name="cb-fade">
              <span v-if="!modern && faceWarning" class="game-stage__chip" role="status">
                <AppIcon name="warning" :size="16" /> Лицо не в кадре
              </span>
            </Transition>
          </template>
        </CornerFrame>

        <GamePreparationBanner :kind="preparationKind" :mode="mode" :countdown="countdown" :fading="startBannerFading" />

        <div v-if="alertVisible" class="game-stage__end-signal" role="status">
          <span aria-hidden="true">!</span><span class="cb-sr-only">Обнаружено возможное нарушение</span>
        </div>

        <Transition name="cb-fade">
          <div v-if="checking && !alertVisible && !postRoundStage" class="game-stage__checking" role="status" aria-live="polite">
            <GlowRing :size="132" :rings="2" pulse>
              <span class="cb-spinner game-stage__checking-spinner" aria-hidden="true"></span>
            </GlowRing>
            <p class="game-stage__checking-title">Проверка…</p>
            <p class="game-stage__checking-text">{{ checkingText }}</p>
          </div>
        </Transition>
      </div>

      <div v-if="compactLives" class="game-post-actions">
        <p class="game-post-actions__notice" role="status">{{ actionNotice }}</p>
        <div class="game-post-actions__buttons">
          <AppButton
            block
            size="sm"
            :variant="sentRematch ? 'secondary' : 'primary'"
            :disabled="!postRoundActionsEnabled || sentRematch || rematchBusy"
            :loading="rematchBusy"
            @click="rematchPending ? acceptRematch() : offerRematch()"
          >{{ sentRematch ? 'Реванш предложен' : rematchPending ? 'Принять реванш' : 'Предложить реванш' }}</AppButton>
          <AppButton block variant="secondary" size="sm" :disabled="!postRoundActionsEnabled || rematchBusy" @click="finishPostRound">Далее</AppButton>
        </div>
      </div>
      <AppButton
        v-else
        block
        class="game-stage__leave"
        :class="{ 'game-stage__leave--hidden': postRoundStage === 'signal' }"
        :variant="modern ? 'ghost' : 'primary'"
        :size="modern ? 'sm' : 'lg'"
        :disabled="Boolean(postRoundStage)"
        @click="askLeave"
      >Уйти</AppButton>
    </section>

    <Transition name="game-intro-fade">
      <div v-if="phase === 'intro'" class="game-screen__intro">
        <GameIntroPanel
          :opponent="opponent"
          :me="me"
          :progress="connectionProgress"
          :status="introStatus"
          :can-leave="waitingLong"
          :compact="compact"
          @leave="askLeave"
        />
      </div>
    </Transition>

    <GameRematchDialog v-if="showRematchDialog" :sent="sentRematch" :busy="rematchBusy" :opponent-name="opponentName" :seconds="postRoundSeconds"
      @accept="acceptRematch" @decline="declineRematch" @cancel="cancelRematch" />

    <GameStatusOverlay
      v-if="overlay"
      :icon="overlay.icon"
      :tone="overlay.tone"
      :title="overlay.title"
      :text="overlay.text"
      :auto-ms="overlay.autoMs"
      :actions="overlay.actions"
      @action="onOverlayAction"
    />

    <BottomSheet v-model="leaveOpen" title="Уйти из игры?">
      <p class="game-screen__sheet-text">Игра будет отменена, соперник получит уведомление. Победа не засчитается никому.</p>
      <template #footer>
        <AppButton block @click="confirmLeave">Уйти</AppButton>
        <AppButton block variant="secondary" size="md" @click="leaveOpen = false">Остаться</AppButton>
      </template>
    </BottomSheet>

    <component
      :is="GameDevTools"
      v-if="GameDevTools && (phase === 'playing' || phase === 'calibration')"
      :metrics="devMetrics"
      :mode="mode"
      @simulate="simulateViolation"
    />
  </div>
</template>

<script setup>
import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref } from "vue";
import AppButton from "@/components/ui/AppButton.vue";
import AppIcon from "@/components/ui/AppIcon.vue";
import BottomSheet from "@/components/ui/BottomSheet.vue";
import CornerFrame from "@/components/ui/CornerFrame.vue";
import GlowRing from "@/components/ui/GlowRing.vue";
import { formatTimer } from "@/utils/format";
import FaceGuideOverlay from "./game/FaceGuideOverlay.vue";
import GameCenterBar from "./game/GameCenterBar.vue";
import GameIntroPanel from "./game/GameIntroPanel.vue";
import GamePreparationBanner from "./game/GamePreparationBanner.vue";
import GameMomentPanel from "./game/GameMomentPanel.vue";
import GameRematchOverlay from "./game/GameRematchOverlay.vue";
import GameRematchDialog from "./game/GameRematchDialog.vue";
import GameStatusOverlay from "./game/GameStatusOverlay.vue";
import { useGameSession } from "./game/useGameSession";

const localVideo = ref(null);
const remoteVideo = ref(null);
const GameDevTools =
  process.env.VUE_APP_DEV_TOOLS === "true"
    ? defineAsyncComponent(() => import(/* webpackChunkName: "dev-tools" */ "./game/GameDevTools.vue"))
    : null;

const {
  mode, me, opponent, phase, checking, checkingSource, alertVisible, startBanner, startBannerFading, countdown, countdownProgress,
  elapsed, faceDetected, faceWarning, modelStatus, remoteVisible, rtcState,
  connectionProgress, introStatus, waitingLong, overlay, devMetrics,
  leaveGame, simulateViolation, onOverlayAction, onRemoteFrame,
  postRoundStage, postRoundSeconds, replayFrameUrl, replayPlaying, replayHolding, replayZoomProgress, replayRegion, replayKeyFrameOnly, replayUnavailable,
  loserName, lossReason, rematchInitiatorId, rematchProgress, rematchPending,
  rematchBusy, postRoundNotice, postRoundActionsEnabled,
  offerRematch, acceptRematch, declineRematch, cancelRematch, finishPostRound, onReplayImageError,
} = useGameSession({ localVideo, remoteVideo });

const leaveOpen = ref(false);
const compact = ref(false);
// Layout only changes geometry; live video DOM nodes and media streams stay mounted.
const layout = ref("C");
const preparationKind = computed(() => phase.value === "instruction" ? "instruction" : startBanner.value ? "start" : phase.value === "calibration" && countdownProgress.value < 1 ? "countdown" : "");
const modern = computed(() => phase.value === "playing" || Boolean(postRoundStage.value));
const showRematchDialog = computed(() => rematchPending.value && postRoundStage.value === "chat");
const compactLives = computed(() => ["suspense", "reveal", "hold", "chat"].includes(postRoundStage.value));
const modeLabel = mode === "MOUTH" ? "Открытый рот" : "Гляделки";
const opponentName = computed(() => opponent.value ? opponent.value.playerName : "Соперник");
const selfName = computed(() => me.value && me.value.playerName ? `${me.value.playerName} · Вы` : "Вы");
const sentRematch = computed(() => isRematchInitiator(me.value));
const actionNotice = computed(() => {
  if (postRoundNotice.value) return postRoundNotice.value;
  if (sentRematch.value) return "Ждём ответа соперника";
  if (rematchPending.value) return "Соперник предлагает реванш";
  if (replayPlaying.value) return "Сначала посмотрим момент проигрыша";
  if (replayHolding.value) return "Ключевой кадр нарушения";
  return "";
});

function isRematchInitiator(player) {
  return Boolean(rematchPending.value && player && player.userId != null && rematchInitiatorId.value != null
    && String(player.userId) === String(rematchInitiatorId.value));
}
function toggleLayout() {
  if (!postRoundStage.value) layout.value = layout.value === "B" ? "C" : "B";
}

const stageTitle = computed(() => {
  if (phase.value === "calibration") return countdownProgress.value >= 1 ? "Ждём готовности соперника" : mode === "MOUTH" ? "Откройте рот!" : "Калибровка участников";
  if (phase.value === "playing") return mode === "MOUTH" ? "Не закрывай рот!" : "Не моргай!";
  if (phase.value === "ended") return "Игра окончена";
  return "Подготовка";
});
const centerMode = computed(() => phase.value === "calibration" ? "countdown" : phase.value === "playing" ? "timer" : "idle");
const centerValue = computed(() => phase.value === "calibration" ? String(countdown.value) : phase.value === "playing" ? formatTimer(elapsed.value) : "•");
const centerProgress = computed(() => phase.value === "calibration" ? countdownProgress.value : phase.value === "playing" ? 1 : 0);
const centerRing = computed(() => phase.value === "calibration" ? 1 - countdownProgress.value : phase.value === "playing" ? ((elapsed.value % 60) + 1) / 60 : 0);
// Calibration remains unchanged. During the game, use the compact status without obscuring the face.
const showLocalGuide = computed(() => !modern.value && (phase.value === "calibration"
  || (phase.value === "playing" && modelStatus.value === "ready" && !faceDetected.value)));
const connectionWeak = computed(() => (phase.value === "calibration" || phase.value === "playing")
  && ["disconnected", "failed"].includes(rtcState.value));
const checkingText = computed(() => checkingSource.value === "me" ? "Проверяем ваш кадр"
  : checkingSource.value === "opponent" ? "Проверяем кадр соперника" : "Сервер проверяет нарушение");

function askLeave() { leaveOpen.value = true; }
function confirmLeave() { leaveOpen.value = false; leaveGame(); }
function measure() { compact.value = window.innerHeight < 720; }
onMounted(() => { measure(); window.addEventListener("resize", measure); });
onBeforeUnmount(() => { window.removeEventListener("resize", measure); });
</script>

<style scoped>
.game-screen {
  gap: 0;
  overflow: hidden;
}

/* ------------------------------------------------------------ сцена */

.game-stage {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Заголовок по центру экрана (как в макете): кнопка «назад» слева, справа — пустая колонка той же ширины. */
.game-stage__header {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) 48px;
  align-items: center;
  column-gap: 6px;
  height: 52px;
  flex-shrink: 0;
}

.game-stage__back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  padding: 0;
  border-radius: 14px;
  border: 1px solid rgba(221, 245, 90, 0.22);
  background: linear-gradient(180deg, #1c2316 0%, #141a10 100%);
  color: var(--cb-lime);
  box-shadow: var(--cb-inner-highlight);
  cursor: pointer;
  transition: transform var(--cb-dur-fast) var(--cb-ease), border-color var(--cb-dur) var(--cb-ease);
}

.game-stage__back:active {
  transform: scale(0.95);
  border-color: var(--cb-lime-border);
}

/* «Калибровка участников» целиком при ширине экрана 360-430 px (многоточие — только на совсем узких). */
.game-stage__title {
  min-width: 0;
  margin: 0;
  font-size: clamp(17px, 4.9vw, 20px);
  font-weight: 700;
  text-align: center;
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.game-stage__videos {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.game-stage__frame {
  flex: 1 1 0;
  min-height: 0;
}

.game-stage__center {
  margin: -14px 0;
  z-index: 3;
}

.game-stage__video {
  background: #0d100b;
}

.game-stage__video--mirror {
  transform: scaleX(-1);
}

.game-stage__waiting {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border-radius: 22px;
  background: radial-gradient(70% 70% at 50% 50%, rgba(28, 35, 22, 0.9) 0%, rgba(11, 14, 10, 0.96) 100%);
  color: var(--cb-text-2);
  font-size: 14px;
  font-weight: 500;
}

.game-stage__waiting-spinner {
  font-size: 30px;
}

.game-stage__name {
  position: absolute;
  left: 50%;
  bottom: 12px;
  z-index: 3;
  max-width: 60%;
  transform: translateX(-50%);
  padding: 4px 12px;
  border-radius: var(--cb-radius-pill);
  background: rgba(11, 14, 10, 0.6);
  border: 1px solid var(--cb-border);
  color: var(--cb-text);
  font-size: 13px;
  font-weight: 600;
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
}

.game-stage__name--top {
  top: 12px;
  bottom: auto;
}

.game-stage__chip {
  position: absolute;
  top: 14px;
  left: 50%;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transform: translateX(-50%);
  padding: 7px 14px;
  border-radius: var(--cb-radius-pill);
  background: rgba(30, 14, 10, 0.82);
  border: 1px solid rgba(255, 107, 90, 0.55);
  color: #ffd2cb;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.45);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
}

.game-stage__chip--below {
  top: 50px;
}

.game-stage__checking {
  position: absolute;
  inset: 0;
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 22px;
  background: rgba(5, 7, 5, 0.7);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  text-align: center;
}

.game-stage__checking-spinner {
  font-size: 46px;
}

.game-stage__checking-title {
  font-size: 28px;
  font-weight: 800;
  color: var(--cb-lime);
  text-shadow: var(--cb-text-glow);
}

.game-stage__checking-text {
  color: var(--cb-text-2);
  font-size: 15px;
}

.game-stage__leave {
  flex-shrink: 0;
}

/* ------------------------------------------------------------ intro */

.game-screen__intro {
  position: absolute;
  inset: 0;
  z-index: 5;
  padding:
    calc(var(--cb-safe-top) + 12px)
    calc(var(--cb-safe-right) + var(--cb-gutter))
    calc(var(--cb-safe-bottom) + 12px)
    calc(var(--cb-safe-left) + var(--cb-gutter));
  background:
    radial-gradient(120% 60% at 50% 0%, var(--cb-bg-glow) 0%, rgba(198, 255, 58, 0) 60%),
    var(--cb-bg);
}

.game-intro-fade-leave-active {
  transition: opacity 0.45s var(--cb-ease), transform 0.45s var(--cb-ease);
}

.game-intro-fade-leave-to {
  opacity: 0;
  transform: scale(1.03);
}

.game-screen__sheet-text {
  color: var(--cb-text-2);
  font-size: 15px;
  line-height: 1.45;
}

@media (max-height: 700px) {
  .game-stage {
    gap: 6px;
  }

  .game-stage__header {
    grid-template-columns: 42px minmax(0, 1fr) 42px;
    height: 44px;
  }

  .game-stage__back {
    width: 42px;
    height: 42px;
  }
}

/* The active round and post-round reuse the original live video nodes. */
.game-screen--modern {
  --game-rematch-green: #9ddf69;
  --game-rematch-wash: rgba(112, 195, 71, 0.36);
  height: var(--tg-viewport-stable-height, var(--cb-vh));
  min-height: var(--tg-viewport-stable-height, var(--cb-vh));
}
.game-screen--modern .game-stage { gap: 8px; }
.game-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto 44px;
  grid-template-rows: 24px 16px;
  align-items: center;
  column-gap: 12px;
  flex: 0 0 44px;
  min-width: 0;
}
.game-toolbar__mode {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--cb-text);
  font-size: 14px;
  font-weight: 600;
  min-width: 0;
  white-space: nowrap;
}
.game-toolbar__mode .app-icon { color: var(--cb-lime); flex: 0 0 auto; }
.game-toolbar__status {
  grid-column: 1;
  grid-row: 2;
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--cb-text-2);
  font-size: 11px;
  line-height: 16px;
}
.game-status-dot {
  display: inline-block;
  flex: 0 0 7px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--cb-lime);
}
.game-toolbar__status--warning .game-status-dot { background: var(--cb-gold); }
.game-toolbar__time { grid-column: 2; grid-row: 1 / 3; color: var(--cb-text); font-size: 18px; font-weight: 700; }
.game-toolbar__layout {
  grid-column: 3;
  grid-row: 1 / 3;
  height: 44px;
  min-width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 1px solid var(--cb-border-strong);
  border-radius: 10px;
  background: var(--cb-surface);
  color: var(--cb-lime);
  font-size: 11px;
  cursor: pointer;
}
.game-toolbar__layout:disabled { cursor: default; }
.game-toolbar__layout-icon { display: flex; flex-direction: column; gap: 3px; width: 16px; height: 23px; }
.game-toolbar__layout-icon i { display: block; flex: 1 1 0; border: 1px solid currentColor; border-radius: 2px; }
.game-toolbar__layout-icon--large i:first-child { flex: 2 1 0; }
.game-toolbar--moment { display: flex; justify-content: center; }
.game-toolbar__moment-title { font-size: clamp(20px, 6vw, 24px); font-weight: 700; }
.game-screen--modern .game-stage__frame {
  position: absolute;
  left: 0;
  width: 100%;
  height: calc(50% - 4px);
  flex: none;
  overflow: hidden;
  box-shadow: none;
  border: 1px solid var(--cb-border);
  transition: top 240ms var(--cb-ease), left 240ms var(--cb-ease), width 240ms var(--cb-ease), height 240ms var(--cb-ease);
}
.game-screen--modern .game-stage__frame--remote { top: 0; }
.game-screen--modern .game-stage__frame--local { top: calc(50% + 4px); }
.game-screen--modern .game-stage__name {
  left: 8px;
  bottom: 8px;
  top: auto;
  transform: none;
  max-width: calc(100% - 16px);
  padding: 4px 7px;
  border: 0;
  border-radius: 7px;
  font-size: 12px;
  background: var(--cb-overlay);
}
.game-screen--modern .game-stage__frame--local .game-stage__name { max-width: 46%; }
.game-stage__face-status {
  position: absolute;
  bottom: 8px;
  right: 8px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 49%;
  padding: 4px 7px;
  border-radius: 7px;
  color: var(--cb-lime);
  background: var(--cb-overlay);
  font-size: 11px;
  white-space: nowrap;
}
.game-stage__face-status--warning { color: var(--cb-gold); }
.game-stage__face-status--warning .game-status-dot { background: var(--cb-gold); }
.game-screen--modern.game-screen--layout-C:not(.game-screen--compact-lives) .game-stage__frame--remote {
  height: calc(74% - 8px);
}
.game-screen--modern.game-screen--layout-C:not(.game-screen--compact-lives) .game-stage__frame--local {
  top: 74%;
  height: 26%;
  border-color: var(--cb-border-strong);
}
.game-screen--modern.game-screen--layout-C:not(.game-screen--compact-lives) .game-stage__frame--local :deep(.corner-frame__content) {
  inset: 8px 54% 8px 8px;
  border-radius: 11px;
}
.game-screen--modern.game-screen--layout-C:not(.game-screen--compact-lives) .game-stage__frame--local .game-stage__name {
  left: 49%;
  bottom: 50%;
  max-width: 48%;
  padding: 0 6px 0 0;
  background: none;
  font-size: 14px;
  -webkit-backdrop-filter: none;
  backdrop-filter: none;
}
.game-screen--modern.game-screen--layout-C:not(.game-screen--compact-lives) .game-stage__face-status {
  left: 49%;
  top: calc(50% + 8px);
  bottom: auto;
  right: 4px;
  max-width: none;
  padding: 0;
  background: none;
  white-space: normal;
}
.game-screen--modern .game-stage__leave { height: 44px; min-height: 44px; box-shadow: none; color: var(--cb-text-2); }
.game-stage__leave--hidden { visibility: hidden; }
.game-stage__end-signal {
  position: absolute;
  inset: -3px;
  z-index: 4;
  display: grid;
  place-items: center;
  border: 2px solid transparent;
  border-radius: 18px;
  pointer-events: none;
  border-color: var(--cb-gold);
  animation: game-end-pulse 1s ease-in-out infinite;
}
.game-stage__end-signal > span:first-child {
  display: grid;
  place-items: center;
  width: min(86%, 320px);
  height: min(86vw, 320px);
  border-radius: 50%;
  color: var(--cb-gold);
  background: rgba(5, 8, 3, .64);
  text-shadow: 0 3px 4px #090e04, 0 0 32px rgba(246,195,67,.8);
  -webkit-text-stroke: 3px rgba(9,14,4,.9);
  font-size: clamp(180px, 64vw, 288px);
  line-height: 1;
  font-weight: 800;
  opacity: .96;
  animation: game-end-mark 1s ease-in-out infinite;
}
.game-stage__moment {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: calc(65% - 44px);
}
.game-stage__live-status {
  position: absolute;
  top: calc(65% - 32px);
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: 24px;
  border-top: 1px solid var(--cb-border-strong);
  padding-top: 8px;
  font-size: 13px;
}
.game-stage__live-status > span:first-child { display: flex; align-items: center; gap: 7px; font-weight: 600; }
.game-stage__deadline { color: var(--cb-lime); font-weight: 700; }
.game-stage__live-hint { color: var(--cb-text-2); font-size: 12px; }
.game-screen--compact-lives .game-stage__frame { top: 65%; width: calc(50% - 4px); height: 35%; }
.game-screen--compact-lives .game-stage__frame--local { left: calc(50% + 4px); }
.game-screen--compact-lives .game-stage__frame--local .game-stage__name { max-width: calc(100% - 16px); }
.game-stage__live-badge {
  position: absolute;
  top: 7px;
  left: 7px;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 3px 5px;
  border-radius: 6px;
  color: var(--cb-text);
  background: var(--cb-overlay);
  font-size: 11px;
}
.game-post-actions { flex: 0 0 auto; display: flex; flex-direction: column; gap: 6px; }
.game-post-actions__notice { min-height: 18px; color: var(--cb-text-2); text-align: center; font-size: 12px; line-height: 18px; }
.game-post-actions__buttons { display: grid; grid-template-columns: minmax(0, 7fr) minmax(78px, 3fr); gap: 8px; }
.game-post-actions__buttons .app-button {
  min-height: 48px;
  height: auto;
  padding: 10px 8px;
  border-radius: 14px;
  box-shadow: none;
  line-height: 1.2;
  font-size: 14px;
}
.game-post-actions__buttons :deep(.app-button__label) { white-space: normal; text-overflow: clip; }
@keyframes game-end-pulse {
  0%, 100% { border-color: var(--cb-gold); box-shadow: 0 0 12px rgba(246,195,67,.55), inset 0 0 8px rgba(246,195,67,.35); }
  50% { border-color: var(--cb-gold); box-shadow: 0 0 32px rgba(246,195,67,.85), inset 0 0 18px rgba(246,195,67,.55); }
}
@keyframes game-end-mark {
  0%, 100% { opacity: .86; transform: scale(.98); }
  50% { opacity: 1; transform: scale(1.03); }
}
@media (max-height: 560px) {
  .game-screen--compact-lives .game-toolbar { flex-basis: 32px; }
  .game-screen--compact-lives .game-stage { gap: 6px; }
  .game-screen--compact-lives .game-stage__name { bottom: 4px; left: 4px; font-size: 11px; }
  .game-stage__live-badge { top: 4px; left: 4px; font-size: 10px; }
  .game-post-actions__notice { min-height: 16px; font-size: 11px; line-height: 16px; }
}
@media (max-width: 340px) {
  .game-toolbar { column-gap: 8px; }
  .game-toolbar__mode { gap: 5px; font-size: 13px; }
  .game-screen--modern .game-stage__frame--local .game-stage__name { max-width: 41%; }
  .game-screen--compact-lives .game-stage__frame--local .game-stage__name { max-width: calc(100% - 8px); }
  .game-stage__face-status { font-size: 10px; max-width: 56%; padding: 4px 5px; }
  .game-post-actions__buttons .app-button { font-size: 13px; }
}
@media (prefers-reduced-motion: reduce) {
  .game-screen--modern .game-stage__frame { transition: none; }
  .game-stage__end-signal { animation: none; border-color: var(--cb-gold); }
  .game-stage__end-signal > span:first-child { animation: none; opacity: 1; }
}

</style>
